<script setup lang="ts">
  import type { QuotedPlanVm } from '~/models/quoting/QuotedPlan'
  import { AdditionalBenefitType, PlanBenefitType } from '~/generated/api-clients-generated'
  import { ProfileStore } from '~/stores/profile'

  const props = defineProps<{
    quote: QuotedPlanVm
    id: string
    hideHeader?: boolean
    simple?: boolean
  }>()

  const { getId } = Id.use(props.id)
  const { show } = DialogController.use()

  const { additionalBenefitOptions } = await AdditionalBenefits.use('compact')
  const { benefitMap } = await BenefitDetailsMap.use()

  const profile = ProfileStore.use()

  const showBenefitSidebar = (benefit: { covered: boolean; sidebarId: string }) => {
    if (benefit.covered) {
      show(benefit.sidebarId)
    }
  }

  const benefits = computed(() =>
    additionalBenefitOptions.value.map((abo) => ({
      ...abo,
      requested: profile.needsAssessment.additionalBenefits?.includes(abo.value),
      text: abo.value === AdditionalBenefitType.OtcMedications ? 'Over the Counter' : abo.text,
      details: benefitMap.value.find((b) => b.id === abo.value)?.getter(props.quote),
      covered: props.quote.details.selectedBenefits[PlanBenefitType[abo.planBenefitType]],
      sidebarId: getId.value(`${abo.value}-benefit-details-sidebar`)
    }))
  )
</script>

<template>
  <section id="benefits-guided" class="flex w-full scroll-mt-28 flex-col">
    <header class="content-section-header bg-gray-200" v-if="!hideHeader && !simple">
      <div>
        <h2>Additional Benefits</h2>
      </div>
    </header>

    <ul class="flex w-full flex-wrap gap-2 py-4">
      <li v-for="benefit in benefits" :key="benefit.value" class="flex-1">
        <HpButton
          variant="none"
          @click="showBenefitSidebar(benefit)"
          class="relative min-h-0 rounded-xl border bg-white px-2 py-1.5 text-base font-semibold"
          :class="[
            {
              'tag-shadow cursor-pointer border-border': benefit.covered
            },
            {
              'tag-shadow cursor-pointer border-success/25 !bg-success-light/50 transition-colors hover:border-success/60':
                benefit.covered && simple
            },
            { 'cursor-auto select-none border-border text-gray-500 line-through': !benefit.covered }
          ]"
        >
          <HpIcon
            v-if="benefit.requested && !simple"
            name="heart-filled-circle"
            :class="['requested-benefit-marker', { 'not-covered': !benefit.covered }]"
          />
          <HpIcon
            v-if="benefit.requested && !simple"
            name="heart-filled-circle"
            :class="['requested-benefit-marker', { 'not-covered': !benefit.covered }]"
          />
          <HpIcon
            v-if="!simple"
            :name="benefit.icon"
            class="mr-1 size-7"
            :class="[{ 'text-primary': benefit.covered }, { 'text-gray-500': !benefit.covered }]"
          />
          <HpIcon v-else-if="benefit.covered" name="yes" class="text-success" />
          <span class="whitespace-nowrap">{{ benefit.text }}</span>
        </HpButton>
      </li>
    </ul>
    <QuoteCardBenefitDetailsSidebar
      v-for="ben in benefits"
      :icon="ben.icon"
      :label="ben.text"
      :id="ben.sidebarId"
      :details="ben.details"
    />
  </section>
</template>

<style scoped>
  .requested-benefit-marker {
    @apply absolute -left-2 -top-3;

    &.not-covered {
      :deep(rect) {
        @apply fill-gray-300;
      }

      :deep(path) {
        @apply fill-gray-600;
      }
    }
  }

  .tag-shadow {
    @apply shadow-[0px_4px_8px_rgba(111,_114,_133,_0.2)];
  }
</style>
